import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
	Box,
	Button,
	CheveronSmallLeftOutline,
	DotsHorizontalOutline,
	Flex,
	HStack,
	IconButton,
	Input,
	InputGroup,
	InputLeftElement,
	Menu,
	MenuButton,
	MenuDivider,
	MenuItem,
	MenuList,
	PencilAltOutline,
	PencilOutline,
	SearchAltOutline,
	Text,
	TrashOutline,
	VStack,
	XOutline,
} from '@bonusly/design-system';

import { useBizyUserConversation, useBizyUserConversations } from '../../queries/bizy-conversations';
import { useBizyWidgetStore } from '../bizy-widget/store';
import { BizySidePanelHeader } from './bizy-side-panel-header';
import { getPageDisplayName } from './lib/bizy-page-config';
import { generateConversationTitle } from './lib/conversation-title';
import { getCachedTitle, removeCachedTitle, setCachedTitle } from './lib/conversation-title-cache';
import { useBizySidePanelStore } from './store/use-bizy-side-panel-store';

interface BizySidePanelHistoryViewProps {
	onBack: () => void;
	onNewConversation: () => void;
}

function formatLastUpdated(date: Date): string {
	return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

export function BizySidePanelHistoryView({ onBack, onNewConversation }: BizySidePanelHistoryViewProps) {
	const { t } = useTranslation('admin_bizy');
	const [searchValue, setSearchValue] = useState('');
	const [selectedConversationId, setSelectedConversationId] = useState<string | undefined>();
	const [renamingId, setRenamingId] = useState<string | undefined>();
	const [renameValue, setRenameValue] = useState('');
	const setResume = useBizyWidgetStore((s) => s.setResume);

	const closePanel = useBizySidePanelStore((state) => state.close);
	const { data: conversationsData, isLoading } = useBizyUserConversations();
	const { data: selectedConversation } = useBizyUserConversation(selectedConversationId);
	const conversations = conversationsData?.[0] || [];

	useEffect(() => {
		if (!selectedConversation || !selectedConversationId) return;
		setResume(selectedConversationId, selectedConversation.messages);
		onBack();
	}, [onBack, setResume, selectedConversation, selectedConversationId]);

	const filteredConversations = useMemo(() => {
		if (!searchValue.trim()) return conversations;
		const query = searchValue.toLowerCase();
		return conversations.filter((conversation) => conversation.firstUserMessage.toLowerCase().includes(query));
	}, [conversations, searchValue]);

	const handleRename = (conversationId: string) => {
		const cached = getCachedTitle(conversationId);
		const conversation = conversations.find((c) => c.conversationId === conversationId);
		setRenameValue(cached || generateConversationTitle(conversation?.firstUserMessage ?? ''));
		setRenamingId(conversationId);
	};

	const handleFinishRename = () => {
		if (renamingId && renameValue.trim()) {
			setCachedTitle(renamingId, renameValue.trim());
		}
		setRenamingId(undefined);
		setRenameValue('');
	};

	const handleDelete = (conversationId: string) => {
		removeCachedTitle(conversationId);
	};

	return (
		<Flex direction="column" h="100%">
			<BizySidePanelHeader
				left={
					<HStack spacing={2}>
						<IconButton
							aria-label="Back"
							icon={<CheveronSmallLeftOutline boxSize={5} />}
							size="sm"
							bg="secondary.bgAlpha"
							color="secondary.fg"
							_hover={{ bg: 'secondary.bgAlphaHover' }}
							onClick={onBack}
						/>
						<Text fontSize="md" fontWeight="semibold" color="text.default">
							{t('sidePanel.historyTitle')}
						</Text>
					</HStack>
				}
				right={
					<IconButton
						aria-label="Close Bizy"
						icon={<XOutline boxSize={4} />}
						size="sm"
						bg="secondary.bgAlpha"
						color="secondary.fg"
						_hover={{ bg: 'secondary.bgAlphaHover' }}
						onClick={closePanel}
					/>
				}
			/>

			<Box px={4} pt={2} pb={3}>
				<InputGroup size="sm">
					<InputLeftElement pointerEvents="none" h="100%" pl={2}>
						<SearchAltOutline boxSize={4} color="text.softest" />
					</InputLeftElement>
					<Input
						value={searchValue}
						onChange={(event) => setSearchValue(event.target.value)}
						placeholder={t('sidePanel.searchPlaceholder')}
						borderRadius="12px"
						bg="bg.canvas"
						borderColor="border.default"
						py={2}
						pr={3}
						h="auto"
					/>
				</InputGroup>
			</Box>

			<Box px={4} pb={3}>
				<Button
					size="sm"
					variant="alpha"
					colorScheme="secondary"
					w="100%"
					leftIcon={<PencilAltOutline boxSize={4} />}
					onClick={onNewConversation}
					borderRadius="10px"
				>
					{t('sidePanel.newChat')}
				</Button>
			</Box>

			<Box flex={1} overflowY="auto" px={3} pb={3}>
				<VStack align="stretch" spacing={0}>
					{isLoading && (
						<Text fontSize="sm" color="text.soft" px={2} py={2}>
							{t('thinking')}
						</Text>
					)}
					{!isLoading &&
						filteredConversations.map((conversation) => {
							const title = getCachedTitle(conversation.conversationId) || generateConversationTitle(conversation.firstUserMessage);
							const isRenaming = renamingId === conversation.conversationId;

							return (
								<Flex
									key={conversation.conversationId}
									align="center"
									w="100%"
									px={2}
									py={2}
									borderRadius="md"
									cursor="pointer"
									_hover={{ bg: 'secondary.bgAlpha' }}
									role="group"
								>
									<Box flex={1} minW={0} onClick={() => setSelectedConversationId(conversation.conversationId)}>
										{isRenaming ? (
											<Input
												value={renameValue}
												onChange={(e) => setRenameValue(e.target.value)}
												onBlur={handleFinishRename}
												onKeyDown={(e) => {
													if (e.key === 'Enter') handleFinishRename();
												}}
												size="sm"
												fontSize="sm"
												fontWeight="semibold"
												variant="outline"
												px={1}
												h="28px"
												borderRadius="8px"
												borderColor="primary.fg"
												borderWidth="1.5px"
												_focus={{ borderColor: 'primary.fg', boxShadow: 'none' }}
												autoFocus
												onClick={(e) => e.stopPropagation()}
											/>
										) : (
											<Text fontSize="sm" fontWeight="semibold" color="text.default" noOfLines={1}>
												{title}
											</Text>
										)}
										<Text fontSize="xs" color="text.softer" mt={0.5}>
											{getPageDisplayName(conversation.pagePath)} · {formatLastUpdated(conversation.lastMessageAt)}
										</Text>
									</Box>
									<Box flexShrink={0}>
										<Menu>
											<MenuButton
												as={IconButton}
												aria-label="More"
												icon={<DotsHorizontalOutline boxSize={4} />}
												variant="ghost"
												size="xs"
												color="text.disabled"
												_groupHover={{ color: 'text.softer' }}
												_hover={{ bg: 'secondary.bgAlphaHover' }}
												onClick={(e: React.MouseEvent) => e.stopPropagation()}
												sx={{ borderRadius: '8px' }}
											/>
											<MenuList minW="140px" py={1} zIndex="popover">
												<MenuItem
													fontSize="sm"
													icon={<PencilOutline boxSize={4} />}
													onClick={(e) => {
														e.stopPropagation();
														handleRename(conversation.conversationId);
													}}
												>
													{t('sidePanel.renameChat')}
												</MenuItem>
												<MenuDivider />
												<MenuItem
													fontSize="sm"
													color="error.fg"
													icon={<TrashOutline boxSize={4} />}
													onClick={(e) => {
														e.stopPropagation();
														handleDelete(conversation.conversationId);
													}}
												>
													{t('sidePanel.deleteChat')}
												</MenuItem>
											</MenuList>
										</Menu>
									</Box>
								</Flex>
							);
						})}
					{!isLoading && filteredConversations.length === 0 && (
						<Text fontSize="sm" color="text.soft" px={2}>
							{t('sidePanel.noHistory')}
						</Text>
					)}
				</VStack>
			</Box>
		</Flex>
	);
}
